import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { MediaItemSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useAuth } from '@fetmode/app-core';
import {
  COMPACT_GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';

export function ContentPage(): ReactElement {
  const { api } = useAuth();
  const [items, setItems] = useState<MediaItemSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceCoins, setPriceCoins] = useState('50');
  const [mediaUrl, setMediaUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const itemsPaging = usePagedItems(items, COMPACT_GRID_PAGE_SIZE);

  const loadItems = async (): Promise<void> => {
    const result = await api.listModelMedia();
    setItems(result.items);
  };

  useEffect(() => {
    let cancelled = false;

    void loadItems()
      .catch(() => {
        if (!cancelled) {
          setError(t('common.studio.content.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const handleCreate = async (): Promise<void> => {
    setIsSaving(true);
    setError(null);

    try {
      await api.createMediaItem({
        title,
        description: description || undefined,
        priceCoins: Number.parseInt(priceCoins, 10),
        mediaUrl,
        thumbnailUrl: thumbnailUrl || undefined,
      });
      setTitle('');
      setDescription('');
      setPriceCoins('50');
      setMediaUrl('');
      setThumbnailUrl('');
      await loadItems();
    } catch {
      setError(t('common.studio.content.createError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async (mediaItemId: string): Promise<void> => {
    setPublishingId(mediaItemId);
    setError(null);

    try {
      await api.publishMediaItem(mediaItemId);
      await loadItems();
    } catch {
      setError(t('common.studio.content.publishError'));
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <section>
      <h1 style={{ fontFamily: 'var(--fm-font-heading)', marginTop: 0 }}>
        {t('common.studio.content.title')}
      </h1>
      <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.content.subtitle')}</p>

      <div className="fm-card" style={{ marginTop: '1.5rem' }}>
        <h2 style={{ marginTop: 0 }}>{t('common.studio.content.createTitle')}</h2>
        <label style={{ display: 'block', marginTop: '0.75rem' }}>
          {t('common.studio.content.titleLabel')}
          <input
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ display: 'block', marginTop: '0.75rem' }}>
          {t('common.studio.content.descriptionLabel')}
          <textarea
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            rows={3}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ display: 'block', marginTop: '0.75rem' }}>
          {t('common.studio.content.priceLabel')}
          <input
            type="number"
            min={5}
            value={priceCoins}
            onChange={(event) => {
              setPriceCoins(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ display: 'block', marginTop: '0.75rem' }}>
          {t('common.studio.content.mediaUrlLabel')}
          <input
            value={mediaUrl}
            onChange={(event) => {
              setMediaUrl(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ display: 'block', marginTop: '0.75rem' }}>
          {t('common.studio.content.thumbnailUrlLabel')}
          <input
            value={thumbnailUrl}
            onChange={(event) => {
              setThumbnailUrl(event.target.value);
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <div style={{ marginTop: '1rem' }}>
          <Button
            label={isSaving ? t('common.studio.content.creating') : t('common.studio.content.create')}
            onClick={() => {
              void handleCreate();
            }}
            disabled={isSaving || title.length === 0 || mediaUrl.length === 0}
          />
        </div>
      </div>

      {error ? <p className="fm-error">{error}</p> : null}

      {isLoading ? (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.content.loading')}</p>
      ) : null}

      {!isLoading && items.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.content.empty')}</p>
      ) : null}

      <ul style={{ listStyle: 'none', margin: '1rem 0 0', padding: 0, display: 'grid', gap: '1rem' }}>
        {itemsPaging.pageItems.map((item) => (
          <li key={item.id} className="fm-card">
            <h3 style={{ margin: 0 }}>{item.title}</h3>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--fm-color-text-muted)' }}>
              {t('common.studio.content.coins').replace('{{amount}}', String(item.priceCoins))} ·{' '}
              {item.status === 'PUBLISHED'
                ? t('common.studio.content.statusPublished')
                : t('common.studio.content.statusDraft')}
            </p>
            {item.status === 'DRAFT' ? (
              <div style={{ marginTop: '0.75rem' }}>
                <Button
                  label={
                    publishingId === item.id
                      ? t('common.studio.content.publishing')
                      : t('common.studio.content.publish')
                  }
                  onClick={() => {
                    void handlePublish(item.id);
                  }}
                  disabled={publishingId === item.id}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <ThumbGridPager
        page={itemsPaging.page}
        total={itemsPaging.total}
        pageSize={COMPACT_GRID_PAGE_SIZE}
        label={t('common.studio.content.title')}
        onPageChange={itemsPaging.setPage}
      />
    </section>
  );
}
